import React, { useState, useEffect } from 'react';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import { useAuth } from '../../../shared/hooks/useAuth';
import api from '../../../shared/utils/api';
import SearchableDropdown from '../../../shared/components/SearchableDropdown';
import SuccessPopup from '../../../shared/components/SuccessPopup';
import ErrorPopup from '../../../shared/components/ErrorPopup';

interface BusinessCustomer {
  Oid: string;
  Code: string;
  Name: string;
  ShortName?: string;
  TinCode?: string;
  IsVatPayer?: boolean;
  Phone?: string;
  Mail?: string;
  BusinessClassOid?: string;
  RegionId?: string;
  RegionSubId?: string;
}

interface BusinessClass {
  Oid: string;
  BusinessClassName?: string;
  Name?: string;
}

const BusinessCustomerPage: React.FC = () => {
  const { userAuth } = useAuth();
  const [code, setCode] = useState<string>('4401395');
  const [businessCustomer, setBusinessCustomer] = useState<BusinessCustomer | null>(null);
  const [businessClasses, setBusinessClasses] = useState<BusinessClass[]>([]);
  const [selectedBusinessClassOid, setSelectedBusinessClassOid] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // BusinessClass жагсаалт авах
  useEffect(() => {
    const fetchBusinessClasses = async () => {
      try {
        const result = await api.get('/api/BusinessClassOid');
        if (result.success && result.data) {
          setBusinessClasses(result.data);
        } else {
          console.error('Error fetching business classes:', result.message);
        }
      } catch (error) {
        console.error('Error fetching business classes:', error);
      }
    };

    fetchBusinessClasses();
  }, []);

  // Code-оор BusinessCustomer мэдээлэл авах
  const fetchBusinessCustomer = async () => {
    if (!code || code.trim() === '') {
      setErrorMessage('Код оруулна уу');
      setShowErrorPopup(true);
      return;
    }

    setLoading(true);
    try {
      const result = await api.get(`/api/business-customer/by-code?code=${encodeURIComponent(code)}`);
      
      if (result.success && result.data) {
        setBusinessCustomer(result.data);
        setSelectedBusinessClassOid(result.data.BusinessClassOid || '');
        setSuccessMessage('BusinessCustomer мэдээлэл амжилттай авлаа');
        setShowSuccessPopup(true);
      } else {
        setErrorMessage(result.message || 'BusinessCustomer олдсонгүй');
        setShowErrorPopup(true);
        setBusinessCustomer(null);
      }
    } catch (error: any) {
      console.error('Error fetching business customer:', error);
      setErrorMessage(error.message || 'BusinessCustomer мэдээлэл авахад алдаа гарлаа');
      setShowErrorPopup(true);
      setBusinessCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  // BusinessCustomer update хийх
  const handleUpdate = async () => {
    if (!businessCustomer) {
      setErrorMessage('Эхлээд BusinessCustomer мэдээлэл авах хэрэгтэй');
      setShowErrorPopup(true);
      return;
    }

    if (!selectedBusinessClassOid || selectedBusinessClassOid.trim() === '') {
      setErrorMessage('Бизнес ангилал сонгоно уу');
      setShowErrorPopup(true);
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        Code: businessCustomer.Code,
        Name: businessCustomer.Name,
        ShortName: businessCustomer.ShortName || '',
        TinCode: businessCustomer.TinCode || '',
        IsVatPayer: businessCustomer.IsVatPayer || false,
        Phone: businessCustomer.Phone || '',
        Mail: businessCustomer.Mail || '',
        BusinessClassOid: selectedBusinessClassOid,
        RegionId: businessCustomer.RegionId || '',
        RegionSubId: businessCustomer.RegionSubId || ''
      };

      const result = await api.post('/api/business-customer', updateData);
      
      if (result.success) {
        setSuccessMessage(result.message || 'BusinessCustomer амжилттай шинэчлэгдлээ');
        setShowSuccessPopup(true);
        // Шинэчлэгдсэн мэдээллийг дахин авах
        await fetchBusinessCustomer();
      } else {
        setErrorMessage(result.message || 'BusinessCustomer шинэчлэхэд алдаа гарлаа');
        setShowErrorPopup(true);
      }
    } catch (error: any) {
      console.error('Error updating business customer:', error);
      setErrorMessage(error.message || 'BusinessCustomer шинэчлэхэд алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setSaving(false);
    }
  };

  // BusinessClass dropdown options
  const businessClassOptions = businessClasses.map(bc => ({
    value: bc.Oid,
    label: bc.BusinessClassName || bc.Name || bc.Oid
  }));

  // Сонгосон BusinessClass-ийн нэр олох
  const selectedBusinessClassName = businessClasses.find(
    bc => bc.Oid === selectedBusinessClassOid
  )?.BusinessClassName || businessClasses.find(
    bc => bc.Oid === selectedBusinessClassOid
  )?.Name || '';

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">BusinessCustomer засах</h1>

          {/* Code оруулах хэсэг */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Код
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Код оруулах (жишээ: 4401395)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={fetchBusinessCustomer}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Уншиж байна...' : 'Мэдээлэл татах'}
                </button>
              </div>
            </div>
          </div>

          {/* BusinessCustomer мэдээлэл харуулах */}
          {businessCustomer && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Мэдээлэл</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Код
                  </label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {businessCustomer.Code}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Нэр
                  </label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {businessCustomer.Name}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Бизнес ангилал *
                  </label>
                  <SearchableDropdown
                    options={businessClassOptions}
                    value={selectedBusinessClassOid}
                    onChange={(value) => setSelectedBusinessClassOid(value)}
                    valueLabel={selectedBusinessClassName}
                    placeholder="Бизнес ангилал сонгох"
                    className="w-full"
                  />
                </div>

                {businessCustomer.ShortName && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Богино нэр
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {businessCustomer.ShortName}
                    </div>
                  </div>
                )}

                {businessCustomer.TinCode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ТИН код
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {businessCustomer.TinCode}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleUpdate}
                    disabled={saving || !selectedBusinessClassOid}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Хадгалж байна...' : 'Хадгалах'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Success Popup */}
          <SuccessPopup
            message={successMessage}
            isVisible={showSuccessPopup}
            onClose={() => setShowSuccessPopup(false)}
            duration={3000}
          />

          {/* Error Popup */}
          <ErrorPopup
            message={errorMessage}
            isVisible={showErrorPopup}
            onClose={() => setShowErrorPopup(false)}
            duration={5000}
          />
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default BusinessCustomerPage;
